import React, { useState } from 'react';
import { MdLockOutline } from 'react-icons/md';

import { Flex, Icon, TextInput } from '@lucidhq/lucidium';
import { useFormikContext } from '@lucidhq/lucidium/components/FormV2';

import { FieldErrorMessage } from '~/components/Shared/FieldErrorMessage';
import { TemplateQuestion } from '~/components/Shared/hooks/useFetchTemplateQuestions';
import { t } from '~/utils/i18n';
import { EnhancedTemplateQuestion } from '~/utils/templateQuestions';

import { CustomKPIData } from './CustomAnswersContainer';
import { QUESTION_LOGIC_ITEMS } from './QuestionLogicDrawer';

const getAnswerIndex = (
  answers: string[],
  question: EnhancedTemplateQuestion,
  key: string
) => {
  if (
    !!question.none_of_the_above_answer &&
    key === QUESTION_LOGIC_ITEMS.NOTA
  ) {
    return answers.length - 1;
  }

  if (
    !!question.none_of_the_above_answer &&
    key === QUESTION_LOGIC_ITEMS.OTHER
  ) {
    return answers.length - 2;
  }

  return answers.length - 1;
};

type CustomAnchoredAnswerProps = {
  answerKey: QUESTION_LOGIC_ITEMS.NOTA | QUESTION_LOGIC_ITEMS.OTHER;
  question: EnhancedTemplateQuestion;
  onSave: (data: Partial<TemplateQuestion>) => void;
};

export const CustomAnchoredAnswer = ({
  answerKey,
  question,
  onSave
}: CustomAnchoredAnswerProps) => {
  const [isDirty, setDirty] = useState<boolean>(false);
  const [isValid, setValid] = useState<boolean>(false);
  const { setFieldValue, values } = useFormikContext<CustomKPIData>();
  const [inputValue, setInputValue] = useState(
    () => question?.[answerKey] ?? ''
  );

  const answerIndex = getAnswerIndex(values.customAnswers, question, answerKey);

  return (
    <>
      <Flex
        lss={{
          flexDirection: 'row',
          gap: '0.625rem',
          justifyContent: 'center',
          alignItems: 'center',
          py: 'md',
          marginLeft: !!question.isBehavioralCustomKPI ? '-2.125rem' : 'none',
          pl: !!question.isBehavioralCustomKPI ? 'none' : 'xxs'
        }}
      >
        <Icon as={MdLockOutline} size={28} lss={{ p: 'xxs' }} />

        <TextInput
          name={answerKey}
          value={inputValue}
          onChange={e => {
            e.stopPropagation();
            const incomingAnswers = !!values?.customAnswers?.length
              ? [...values.customAnswers]
              : [];

            incomingAnswers[answerIndex] = e.target.value;

            setInputValue(e.target.value);

            if (e.target.value !== '') {
              setFieldValue('customAnswers', incomingAnswers);
              onSave({
                answers: incomingAnswers,
                customAnswers: incomingAnswers,
                [answerKey]: e.target.value
              });
            }
          }}
          onBlur={() => {
            setDirty(true);
            setValid(inputValue !== '');
          }}
          lss={{
            p: 'none',
            width: '100%',
            maxWidth: '46rem'
          }}
        />
      </Flex>
      {isDirty && !isValid && (
        <FieldErrorMessage
          message={t('forms.errors.requiredField')}
          lss={{ mt: 'none', paddingLeft: '2.5rem' }}
        />
      )}
    </>
  );
};
